import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  buildCognitoLogoutUrl,
  clearStoredTokens,
  decodeJwtPayload,
  getStoredTokens,
  isAccessTokenUsable,
  refreshCognitoTokens,
  startCognitoLogin,
  storeTokens,
} from './cognito';

const AuthContext = createContext(null);

export const ADMIN_ROLE_GROUPS = [
  'OWNER',
  'SUPPORT_ADMIN',
  'SUPPORT',
  'USER',
  'FW_ENGINEER',
  'FW_MANAGER',
];

export function getAdminRoleState(groups = []) {
  const adminRoles = groups.filter((group) => ADMIN_ROLE_GROUPS.includes(group));
  const activeRole = adminRoles.length === 1 ? adminRoles[0] : null;
  let roleError = '';

  if (adminRoles.length === 0) {
    roleError = 'No admin role group is assigned. One active role is required.';
  } else if (adminRoles.length > 1) {
    roleError = `Multiple admin role groups are assigned: ${adminRoles.join(', ')}. Only one active role is allowed.`;
  }

  return {
    activeRole,
    adminRoles,
    hasSingleAdminRole: adminRoles.length === 1,
    roleError,
  };
}

function userFromTokens(tokens) {
  const payload = decodeJwtPayload(tokens?.idToken);
  if (!payload) return null;
  const groups = Array.isArray(payload['cognito:groups'])
    ? payload['cognito:groups']
    : [];
  const roleState = getAdminRoleState(groups);

  return {
    email: payload.email || payload['cognito:username'] || payload.username || null,
    username: payload['cognito:username'] || payload.username || null,
    givenName: payload.given_name || null,
    familyName: payload.family_name || null,
    locale: payload.locale || null,
    sub: payload.sub || null,
    groups,
    ...roleState,
    raw: payload,
  };
}

export function AuthProvider({ children }) {
  const [tokens, setTokens] = useState(() => getStoredTokens());
  const isAuthenticated = isAccessTokenUsable(tokens);
  const user = useMemo(() => (isAuthenticated ? userFromTokens(tokens) : null), [isAuthenticated, tokens]);

  const value = useMemo(() => {
    return {
      isAuthenticated,
      user,
      tokens,
      login: async (options) => startCognitoLogin(options),
      setTokens: (nextTokens) => {
        storeTokens(nextTokens);
        setTokens(nextTokens);
      },
      refreshSession: async () => {
        const nextTokens = await refreshCognitoTokens(tokens?.refreshToken);
        storeTokens(nextTokens);
        setTokens(nextTokens);
        return nextTokens;
      },
      clearSession: () => {
        clearStoredTokens();
        setTokens(null);
      },
      logout: () => {
        clearStoredTokens();
        setTokens(null);
        const url = buildCognitoLogoutUrl();
        if (url) window.location.assign(url);
      },
    };
  }, [isAuthenticated, tokens, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
